import React, { useEffect, useRef } from "react";
import { TURNSTILE_SITE_KEY } from "../lib/api";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

const ensureScript = () =>
  new Promise((resolve) => {
    if (typeof window === "undefined") return resolve();
    if (window.turnstile) return resolve();
    const existing = document.querySelector(`script[src^="https://challenges.cloudflare.com/turnstile"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    document.head.appendChild(s);
  });

/**
 * Minimal Cloudflare Turnstile widget wrapper.
 * Calls `onToken(token | null)` whenever the verification state changes.
 */
export const Turnstile = ({ onToken, theme = "dark", testId = "turnstile-widget" }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);

  // Keep latest onToken in a ref so the widget callback always uses the
  // freshest closure without triggering re-renders of the Cloudflare widget.
  useEffect(() => {
    onTokenRef.current = onToken;
  }, [onToken]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await ensureScript();
      if (cancelled || !containerRef.current || !window.turnstile) return;
      // Reset any previous render
      if (widgetIdRef.current != null) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (err) { console.warn("Turnstile: failed to remove previous widget", err); }
        widgetIdRef.current = null;
      }
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme,
        appearance: "always",
        callback: (token) => onTokenRef.current && onTokenRef.current(token),
        "expired-callback": () => onTokenRef.current && onTokenRef.current(null),
        "error-callback": () => onTokenRef.current && onTokenRef.current(null),
        "timeout-callback": () => onTokenRef.current && onTokenRef.current(null),
      });
    })();
    return () => {
      cancelled = true;
      if (widgetIdRef.current != null && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (err) { console.warn("Turnstile: failed to remove widget on unmount", err); }
      }
    };
  }, [theme]);

  return <div ref={containerRef} data-testid={testId} className="cf-turnstile-mount" />;
};
